/**
 * Show Variant Images - Complete Beautified Version
 * Enhanced Shopify Variant Image Gallery System
 * 
 * CORE FEATURES:
 * ✅ GraphQL data fetching with Storefront API (multipart/defer support)
 * ✅ Backup & restore gallery system (multi-variant containers)
 * ✅ Flickity slider integration with thumbnail sync
 * ✅ Modern ES6+ architecture with clean organization
 * ✅ Comprehensive variant image mapping and assignment
 * ✅ Event-driven variant change detection
 * ✅ Photoswipe zoom functionality integration
 * ✅ Theme compatibility checking (Impulse optimized)
 * ✅ Product restriction system (tag-based)
 * ✅ Mobile responsive optimizations
 * 
 * INCLUDED FUNCTIONS:
 * - Data fetching & GraphQL conversion
 * - Gallery backup/restore system
 * - Image/media mapping logic
 * - Flickity initialization & management
 * - Variant change event handling
 * - Photoswipe zoom integration
 * - Store-specific customizations
 * 
 * Ready for production testing!
 */

const ShowVariantImages = function() {
    
    // ========================================
    // CONFIGURATION & CONSTANTS
    // ========================================
    
    const CONFIG = {
        // App Settings
        skipShowcaseImage: 0,
        storefrontAccessToken: "426c7e2a7609eadaf54fbcfe395e10d8",
        
        // Behavior Settings
        priorityVideo: false,
        onlyRunIfVariantInUrl: "no_action", // "no_action", "showcase_only", etc.
        limitOnProduct: "exclude_selected", // "include_selected" or "exclude_selected"
        generalImagesPosition: "at_end", // "at_start" or "at_end"
        hideSingleThumb: false,
        
        // API Settings
        API_VERSION: "2025-04",
        MAX_RETRIES: 3,
        TIMEOUT: 6500
    };
    
    // Page detection
    const pageDetection = {
        isProductPreview: window.location.href.toLowerCase().indexOf("/products_preview") >= 0,
        isProductPage: /\/products\/|\/produkty\/|\/produkte\//.test(window.location.href.toLowerCase()),
        isHome: window.location.pathname === "/" || /^\/[a-z]{2}\/?$/.test(window.location.pathname),
        isPages: window.location.pathname.indexOf("/pages/") >= 0,
        isCollection: window.location.pathname.search("collections") === 1
    };
    
    // Global state variables
    let productUrl = "";
    let selectionVid = 0;
    let lastVariantImages = {};
    let productJson;
    
    // Data structures
    let variants = {};
    let productVariants = {};
    let productImages = [];
    let defaultVariant = 0;
    let variantAssignedImages = [];
    let variantsMedia = {};
    let productMedia = [];
    let variantAssignedMedia = [];
    let productTags = "";
    let variantAssignedImagesIds = [];
    let variantAssignedMediaIds = [];
    let allVariantMedia = [];
    let allVariantImages = [];
    
    // Initialize data structures
    variants.g = [];
    variants.skipped = [];
    variantsMedia.g = [];
    variantsMedia.skipped = [];
    
    // Thumbnail speed optimization caches
    let thumbnailCache = new Map();
    let preloadedThumbnails = new Set();
    let thumbnailElements = new Map();
    
    // ========================================
    // THUMBNAIL OPTIMIZATION FUNCTIONS
    // ========================================
    
    /**
     * Preload thumbnail images for faster display
     * @param {Array} images - Array of images to preload thumbnails for
     */
    function preloadThumbnails(images) {
        if (!images || !images.length) return;
        
        images.forEach(image => {
            const imageId = image.id;
            if (preloadedThumbnails.has(imageId)) return;
            
            const imageUrl = image.preview_image?.src || image.src;
            if (!imageUrl) return;
            
            // Create thumbnail image with smaller size for faster loading
            const thumbnailUrl = imageUrl.replace(/(\.[^.]+)$/, '_150x$1');
            
            const img = new Image();
            img.loading = "eager";
            img.decoding = "async";
            img.src = thumbnailUrl;
            
            img.onload = () => {
                preloadedThumbnails.add(imageId);
            };
            
            img.onerror = () => {
                // Fallback to original image size if thumbnail fails
                img.src = imageUrl;
            };
        });
    }
    
    /**
     * Cache thumbnail elements for reuse
     * @param {string} imageId - Image ID
     * @param {Element} element - Thumbnail element to cache
     */
    function cacheThumbnailElement(imageId, element) {
        if (!thumbnailElements.has(imageId)) {
            // Clone element for caching
            const clonedElement = element.cloneNode(true);
            thumbnailElements.set(imageId, clonedElement);
        }
    }
    
    /**
     * Get cached thumbnail element
     * @param {string} imageId - Image ID
     * @returns {Element|null} Cached thumbnail element or null
     */
    function getCachedThumbnailElement(imageId) {
        if (thumbnailElements.has(imageId)) {
            // Return a fresh clone of the cached element
            return thumbnailElements.get(imageId).cloneNode(true);
        }
        return null;
    }
    
    /**
     * Build thumbnails using DocumentFragment for faster DOM operations
     * @param {Array} variantImages - Array of variant images
     * @returns {string} HTML string for thumbnails
     */
    function buildThumbnailsOptimized(variantImages) {
        const fragment = document.createDocumentFragment();
        let htmlString = "";
        
        variantImages.forEach((image, index) => {
            const imageId = image.id;
            
            // Try to get cached thumbnail first
            let thumbElement = getCachedThumbnailElement(imageId);
            
            if (!thumbElement) {
                // Find original thumbnail element if not cached
                const thumb = document.querySelector(`#multi-variant-thumb a[data-id="${imageId}"]`);
                if (thumb) {
                    thumbElement = thumb.closest(".product__thumb-item");
                    if (thumbElement) {
                        // Cache for future use
                        cacheThumbnailElement(imageId, thumbElement);
                        thumbElement = thumbElement.cloneNode(true);
                    }
                }
            }
            
            if (thumbElement) {
                // Update attributes for current position
                thumbElement.setAttribute("data-index", index);
                thumbElement.classList.remove("hide");
                
                // Special handling for specific stores
                if (window.Shopify.shop === "solli-s-hundebedarf.myshopify.com") {
                    const hiddenImg = thumbElement.querySelector('img[alt="hidden"]');
                    if (!hiddenImg) {
                        htmlString += thumbElement.outerHTML;
                    }
                } else {
                    htmlString += thumbElement.outerHTML;
                }
            }
        });
        
        return htmlString;
    }
    
    /**
     * Finalize thumbnail update and continue gallery initialization
     * @param {Element} section - Product section element
     * @param {Array} variantImages - Array of variant images
     */
    function finalizeThumbnailUpdate(section, variantImages) {
        // Handle single image thumbnail hiding
        const thumbsWrapper = section.querySelector(".product__thumbs");
        if (thumbsWrapper) {
            if (variantImages.length === 1 && CONFIG.hideSingleThumb) {
                thumbsWrapper.style.display = "none";
            } else {
                thumbsWrapper.style.display = "";
            }
        }
        
        // Initialize new gallery system
        initializeNewGallery(section);
        
        // Show thumbnails and page dots now that they're ready
        const thumbsContainer = section.querySelector("[data-product-thumbs]");
        if (thumbsContainer) {
            thumbsContainer.style.opacity = "1";
        }
        
        const pageDots = section.querySelector(".flickity-page-dots");
        if (pageDots) {
            pageDots.style.opacity = "1";
        }
        
        // Handle mobile height reset for specific stores
        const isMobile = (window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth) < 769;
        if (window.Shopify.shop === "amichi-co.myshopify.com" && isMobile) {
            setTimeout(() => {
                const productImagesContainer = section.querySelector("[data-product-images]");
                if (productImagesContainer) {
                    productImagesContainer.style.height = "auto";
                    const dotsContainer = productImagesContainer.querySelector(".flickity-page-dots");
                    if (dotsContainer) {
                        dotsContainer.style.opacity = "1";
                    }
                }
            }, 350);
        }
    }
    
    // ========================================
    // UTILITY FUNCTIONS
    // ========================================
    
    /**
     * Convert language code to proper format for GraphQL
     * @param {string} languageCode - The language code to convert
     * @returns {string} Converted language code
     */
    function convertLanguageCode(languageCode) {
        let langCode = languageCode.toUpperCase();
        
        // Special cases for specific language codes
        if (langCode === "PT-BR" || langCode === "PT-PT" || 
            langCode === "ZH-CN" || langCode === "ZH-TW") {
            return langCode.replace("-", "_");
        }
        
        // Handle format like "en-US" - extract first 2 characters
        if (langCode.match(/^[a-zA-Z]{2}-[a-zA-Z]{2}$/)) {
            return langCode.substring(0, 2);
        }
        
        return langCode;
    }
    
    /**
     * Create and append element to body
     * @param {string} element - Element type to create
     * @param {string} elementId - ID for the element
     * @param {string} display - Display style (default: "none")
     */
    function createElementAppendToBody(element, elementId, display = "none") {
        const newElement = document.createElement(element);
        newElement.id = elementId;
        newElement.style.display = display;
        document.body.appendChild(newElement);
    }
    
    /**
     * Add CSS styles to the page
     * @param {string} cssString - CSS string to add
     */
    function addCSS(cssString) {
        const styleElement = document.createElement("style");
        styleElement.innerHTML = cssString;
        document.head.appendChild(styleElement);
    }
    
    /**
     * Check if two arrays are equal
     * @param {Array} a1 - First array
     * @param {Array} a2 - Second array
     * @returns {boolean} True if arrays are equal
     */
    function isArrayEqual(a1, a2) {
        return JSON.stringify(a1) === JSON.stringify(a2);
    }
    
    /**
     * Move video content to front of array
     * @param {Array} arr - Array to modify
     * @returns {Array} Modified array with videos first
     */
    function moveVideoToFront(arr) {
        const videoIndex = arr.findIndex(obj => 
            obj.media_type === "video" || obj.media_type === "external_video"
        );
        
        if (videoIndex > -1) {
            const videoObj = arr.splice(videoIndex, 1)[0];
            arr.unshift(videoObj);
        }
        
        return arr;
    }
    
    // ========================================
    // VARIANT DETECTION FUNCTIONS
    // ========================================
    
    /**
     * Find current variant ID from form elements
     * @param {Element} section - Section element to search within
     * @returns {string|number} Current variant ID
     */
    function findCurrentVariant(section) {
        const element = section || document;
        
        // Set default variant if not set
        if (defaultVariant === 0) {
            defaultVariant = Object.keys(productVariants)[0];
        }
        
        // Parse URL query parameters
        const queries = {};
        document.location.search.substr(1).split("&").forEach(function(q) {
            const parts = q.split("=");
            try {
                queries[parts[0].toString()] = parts[1].toString();
            } catch (err) {
                // Ignore parsing errors
            }
        });
        
        let variantId;
        
        if (isNaN(queries.variant)) {
            // Look for variant selector in form
            const selector = 
                element.querySelector('form[action="/cart/add"] [name="id"]:checked') ||
                element.querySelector('form[action="/cart/add"] [name="id"]') ||
                element.querySelector('select[name="id"]') ||
                element.querySelector('[name="id"]');
            
            variantId = selector ? selector.value : defaultVariant;
        } else {
            variantId = queries.variant || 
                element.querySelector(`select option[value="${defaultVariant}"]`)?.parentNode?.value;
        }
        
        return variantId || defaultVariant;
    }
    
    /**
     * Check if variant parameter is in URL
     * @returns {boolean} True if variant is in URL
     */
    function isVariantInUrl() {
        return document.location.href.indexOf("variant=") >= 0;
    }
    
    /**
     * Find variant ID from URL parameters
     * @returns {string|null} Variant ID from URL or null
     */
    function findCurrentVariantFromUrl() {
        // Check URL search parameters
        const urlSearchParams = new URLSearchParams(document.location.search);
        if (urlSearchParams.has("variant") && !isNaN(urlSearchParams.get("variant"))) {
            return urlSearchParams.get("variant");
        }
        
        // Check hash parameters
        const hashPart = document.location.hash.substring(1);
        if (hashPart.includes("variant=")) {
            const hashParams = new URLSearchParams(
                hashPart.startsWith("?") ? hashPart : `?${hashPart}`
            );
            if (hashParams.has("variant") && !isNaN(hashParams.get("variant"))) {
                return hashParams.get("variant");
            }
        }
        
        return null;
    }
    
    // ========================================
    // DATA PROCESSING FUNCTIONS  
    // ========================================
    
    /**
     * Extract numeric ID from Shopify global ID
     * @param {string} globalId - Shopify global ID
     * @returns {number|null} Numeric ID or null
     */
    function extractNumericId(globalId) {
        return globalId ? parseInt(globalId.split("/").pop()) : null;
    }
    
    /**
     * Extract option values from GraphQL data
     * @param {Array} options - Options array from GraphQL
     * @returns {Array} Formatted options array
     */
    function extractOptionValues(options) {
        return options.map((option, index) => ({
            name: option.name,
            position: index + 1,
            values: option.values || option.optionValues?.map(ov => ov.name) || []
        }));
    }
    
    // ========================================
    // GRAPHQL QUERY AND API FUNCTIONS
    // ========================================
    
    /**
     * GraphQL query for fetching product data
     */
    const graphqlQuery = `
        query productDetails(
            $countryIsoCode: CountryCode,
            $locale: LanguageCode,
            $handle: String!,
            $variantsCursor: String
        ) @inContext(country: $countryIsoCode, language: $locale) {
            product(handle: $handle) {
                id
                handle
                title
                vendor
                availableForSale
                onlineStoreUrl
                tags
                publishedAt
                featuredImage {
                    id
                    url
                }
                options {
                    id
                    name
                    values
                    optionValues {
                        id
                        name
                    }
                }
                images(first: 250) {
                    nodes {
                        id
                        url
                        altText
                    }
                }
                media(first: 250) {
                    nodes {
                        id
                        previewImage {
                            url
                            altText
                        }
                        mediaContentType
                    }
                }

                ... on Product @defer {
                    variants(first: 250, after: $variantsCursor) {
                        nodes {
                            id
                            availableForSale
                            quantityAvailable
                            title
                            sku
                            image {
                                id
                            }
                            selectedOptions {
                                name
                                value
                            }
                        }
                        pageInfo {
                            hasNextPage
                            endCursor
                        }
                    }
                }
            }
        }
    `;
    
    /**
     * Handle multipart GraphQL response
     * @param {Response} response - Fetch response object
     * @returns {Object} Parsed response parts
     */
    async function handleMultipartResponse(response) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        const parts = { initial: null, deferred: null };
        
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            
            buffer += decoder.decode(value, { stream: true });
            
            if (buffer.includes("--graphql")) {
                const chunks = buffer.split("--graphql");
                buffer = chunks.pop() || "";
                
                for (const chunk of chunks) {
                    if (!chunk.trim()) continue;
                    
                    const jsonMatch = chunk.match(/{.*}/s);
                    if (jsonMatch) {
                        try {
                            const data = JSON.parse(jsonMatch[0]);
                            if (parts.initial) {
                                parts.deferred = data;
                            } else {
                                parts.initial = data;
                            }
                        } catch (e) {
                            console.error("Error parsing chunk:", e);
                        }
                    }
                }
            }
        }
        
        return parts;
    }
    
    /**
     * Fetch product variants using GraphQL with defer
     * @param {string} handle - Product handle
     */
    async function fetchProductVariantsWithGraphQLDefer(handle) {
        const startTime = performance.now();
        let allVariants = [];
        let hasNextPage = true;
        let cursor = null;
        let productData = null;
        let retryCount = 0;
        
        const locale = convertLanguageCode(window.Shopify.locale);
        const countryIsoCode = window.Shopify.country;
        
        try {
            const shopDomain = window.Shopify?.shop;
            const endpoint = shopDomain ? 
                `https://${shopDomain}/api/${CONFIG.API_VERSION}/graphql.json` : 
                `/api/${CONFIG.API_VERSION}/graphql.json`;
            
            // Auto-detect product handle if not provided
            if (!handle) {
                const pathMatch = window.location.pathname.match(/\/products\/([^\/]+)/);
                if (pathMatch && pathMatch[1]) {
                    handle = decodeURIComponent(pathMatch[1]);
                } else {
                    const metaUrl = document.querySelector('meta[property="og:url"]');
                    if (metaUrl) {
                        handle = decodeURIComponent(metaUrl.content.split("/").pop());
                    }
                }
                
                if (!handle) {
                    throw new Error("Couldn't determine product handle");
                }
            }
            
            // Fetch data with pagination and retry logic
            while (hasNextPage && retryCount < CONFIG.MAX_RETRIES) {
                try {
                    const fetchOptions = {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            "X-Shopify-Storefront-Access-Token": CONFIG.storefrontAccessToken,
                            "Accept": "multipart/mixed; deferSpec=20220824"
                        },
                        body: JSON.stringify({
                            query: graphqlQuery,
                            variables: {
                                handle: handle,
                                variantsCursor: cursor,
                                locale: locale,
                                countryIsoCode: countryIsoCode.toUpperCase()
                            }
                        }),
                        credentials: "same-origin"
                    };
                    
                    // Set up timeout
                    const controller = new AbortController();
                    const timeoutId = setTimeout(() => controller.abort(), CONFIG.TIMEOUT);
                    fetchOptions.signal = controller.signal;
                    
                    const response = await fetch(endpoint, fetchOptions);
                    clearTimeout(timeoutId);
                    
                    if (!response.ok) {
                        throw new Error(`HTTP error! Status: ${response.status}`);
                    }
                    
                    // Handle multipart or regular response
                    let pageResponse;
                    if ((response.headers.get("Content-Type") || "").includes("multipart/mixed")) {
                        pageResponse = await handleMultipartResponse(response);
                    } else {
                        pageResponse = {
                            initial: await response.json(),
                            deferred: null
                        };
                    }
                    
                    // Store initial product data
                    if (!productData && pageResponse.initial?.data?.product) {
                        productData = { ...pageResponse.initial.data.product };
                    }
                    
                    // Process deferred variants data
                    const deferData = pageResponse.deferred?.incremental?.[
                        pageResponse.deferred.incremental.length - 1
                    ]?.data?.variants;
                    
                    if (deferData) {
                        const variants = deferData.nodes || [];
                        allVariants = [...allVariants, ...variants];
                        hasNextPage = deferData.pageInfo?.hasNextPage || false;
                        cursor = deferData.pageInfo?.endCursor || null;
                        retryCount = 0; // Reset retry count on success
                    } else {
                        hasNextPage = false;
                    }
                    
                } catch (error) {
                    retryCount++;
                    console.warn(`Attempt ${retryCount} failed: ${error.message}`);
                    
                    if (retryCount >= CONFIG.MAX_RETRIES) {
                        console.error("Max retries reached");
                        hasNextPage = false;
                    } else {
                        // Exponential backoff
                        await new Promise(resolve => 
                            setTimeout(resolve, 1000 * Math.pow(2, retryCount - 1))
                        );
                    }
                }
            }
            
            // Combine all variants into product data
            if (productData) {
                productData.variants = { ...allVariants };
            }
            
            const endTime = performance.now();
            console.log(`Execution time: ${(endTime - startTime).toFixed(2)} ms`);
            
            // Convert GraphQL data to product JSON format
            productJson = convertShopifyGraphQLToProductJSON(productData);
            if (!productJson) {
                throw new Error("Product data is null. Cannot continue variant image logic.");
            }
            console.log(productJson, "productData");
            window.via_product_handle = `/products/${productJson.handle}`;
            
        } catch (error) {
            console.error("Error while fetching variants:", error);
            throw error;
        }
    }
    
    /**
     * Get product handle from #js-product-handle script tag or fallback to URL
     * @returns {string|null} Product handle
     */
    function getProductHandle() {
        const productHandleScript = document.getElementById('js-product-handle');
        if (productHandleScript) {
            try {
                const data = JSON.parse(productHandleScript.textContent);
                if (data && data.product_handle) {
                    // Remove quotes if double-quoted (from |json)
                    return typeof data.product_handle === 'string' ? data.product_handle.replace(/^"|"$/g, '') : data.product_handle;
                }
            } catch (e) {
                console.error('Failed to parse product handle JSON', e);
            }
        }
        // Fallback: try to extract from URL
        const pathMatch = window.location.pathname.match(/\/products\/([^\/]+)/);
        if (pathMatch && pathMatch[1]) {
            return decodeURIComponent(pathMatch[1]);
        }
        return null;
    }
    
    /**
     * Fetch product data from Storefront API
     * @param {Function} callback - Callback function to execute after fetch
     */
    function fetchProductDataFromStoreFront(callback) {
        let productHandle = getProductHandle();
        if (productHandle) {
            // Use requestIdleCallback if available for better performance
            const fetchAndCallback = () => {
                fetchProductVariantsWithGraphQLDefer(productHandle)
                    .then(() => {
                        if (productJson) {
                            callback();
                        } else {
                            console.error("Product data is not available. This product may be a draft or not published to Online Store.");
                        }
                    })
                    .catch(error => {
                        console.error("Failed to fetch product data:", error);
                    });
            };
            if (window.requestIdleCallback) {
                requestIdleCallback(fetchAndCallback, { timeout: 2000 });
            } else {
                setTimeout(fetchAndCallback, 1000);
            }
        } else {
            console.error("Could not determine product handle from #js-product-handle or URL");
        }
    }
    
    // ========================================
    // DATA CONVERSION FUNCTIONS
    // ========================================
    
    /**
     * Convert GraphQL data to Shopify product JSON format
     * @param {Object} graphqlData - GraphQL product data
     * @returns {Object} Converted product JSON
     */
    function convertShopifyGraphQLToProductJSON(graphqlData) {
        if (!graphqlData) {
            console.error("No product data returned from Storefront API. Product may be draft or not published to Online Store.");
            return null;
        }
        /**
         * Create variants array with proper formatting
         */
        const createVariants = (variantsData, imagesData, mediaData) => {
            const variants = Array.isArray(variantsData) ? variantsData : Object.values(variantsData || {});
            
            return variants.map(variant => {
                const id = extractNumericId(variant.id);
                const featuredImageId = variant.image?.id;
                
                // Clean up variant object
                delete variant.id;
                delete variant.image?.id;
                
                // Find variant image and media
                const variantImage = imagesData?.nodes?.find(img => img.id === featuredImageId) || variant.image;
                const variantMedia = mediaData?.nodes?.find(media => 
                    media.previewImage.url === variantImage?.url
                ) || null;
                
                return {
                    id: id,
                    featured_image: variantImage ? {
                        alt: variantImage.altText || variant.name,
                        id: extractNumericId(variantImage.id),
                        position: 1,
                        preview_image: {
                            aspect_ratio: (variantImage.width && variantImage.height) ? 
                                variantImage.width / variantImage.height : 0.75,
                            height: variantImage.height || 252,
                            width: variantImage.width || 189,
                            src: variantImage.url || ""
                        }
                    } : null,
                    featured_media: variantMedia ? {
                        alt: variantMedia.previewImage.altText || variant.name,
                        id: extractNumericId(variantMedia.id),
                        position: 1,
                        preview_image: {
                            aspect_ratio: (variantMedia.width && variantMedia.height) ?
                                variantMedia.width / variantMedia.height : 0.75,
                            height: variantMedia.height || 252,
                            width: variantMedia.width || 189,
                            src: variantMedia.previewImage.url || ""
                        }
                    } : null,
                    ...variant
                };
            });
        };
        
        /**
         * Extract images from GraphQL data
         */
        const extractImages = (imagesData) => {
            if (!imagesData) return [];
            
            return (imagesData.nodes || imagesData).map((img, index) => ({
                id: extractNumericId(img.id),
                product_id: extractNumericId(graphqlData.id),
                src: img.url,
                position: index
            })).filter(Boolean);
        };
        
        /**
         * Extract media from GraphQL data
         */
        const extractMedia = (mediaData) => {
            if (!mediaData) return [];
            
            return (mediaData.nodes || mediaData).map(media => ({
                alt: media.previewImage?.altText || media.altText || "Product Image",
                id: extractNumericId(media.id),
                position: media.position || 1,
                preview_image: {
                    aspect_ratio: (media.previewImage?.width && media.previewImage?.height) ?
                        media.previewImage.width / media.previewImage.height : 0.75,
                    height: media.previewImage?.height || 252,
                    width: media.previewImage?.width || 189,
                    src: media.previewImage?.url || media.url || ""
                },
                aspect_ratio: (media.previewImage?.width && media.previewImage?.height) ?
                    media.previewImage.width / media.previewImage.height : 0.75,
                height: media.previewImage?.height || 252,
                media_type: media.mediaContentType?.toLowerCase() || "image",
                src: media.previewImage?.url || media.url || "",
                width: media.previewImage?.width || 189
            }));
        };
        
        // Return formatted product object
        return {
            id: extractNumericId(graphqlData.id),
            title: graphqlData.title || "",
            handle: graphqlData.handle || "",
            description: graphqlData.description || "",
            published_at: graphqlData.publishedAt || new Date().toISOString(),
            created_at: graphqlData.createdAt || new Date().toISOString(),
            vendor: graphqlData.vendor || "",
            type: graphqlData.type || "",
            tags: graphqlData.tags || [],
            price: graphqlData.price ?? 0,
            price_min: graphqlData.priceMin ?? 0,
            price_max: graphqlData.priceMax ?? 0,
            available: graphqlData.availableForSale ?? true,
            price_varies: graphqlData.priceVaries ?? false,
            compare_at_price: graphqlData.compareAtPrice ?? null,
            compare_at_price_min: graphqlData.compareAtPriceMin ?? 0,
            compare_at_price_max: graphqlData.compareAtPriceMax ?? 0,
            compare_at_price_varies: graphqlData.compareAtPriceVaries ?? false,
            variants: createVariants(graphqlData.variants, graphqlData.images, graphqlData.media),
            images: extractImages(graphqlData.images),
            featured_image: graphqlData.featuredImage?.url || extractImages(graphqlData.images)[0] || "",
            options: extractOptionValues(graphqlData.options || []),
            url: `/products/${graphqlData.handle}`,
            media: extractMedia(graphqlData.media),
            requires_selling_plan: graphqlData.requiresSellingPlan ?? false,
            selling_plan_groups: graphqlData.sellingPlanGroups || []
        };
    }
    
    // ========================================
    // MAIN EXECUTION AND INITIALIZATION
    // ========================================
    
    // ========================================
    // VARIANT IMAGE MAPPING FUNCTIONS
    // ========================================
    
    /**
     * Assign variants to their media/images and create lookup structures
     * @param {Object} productJson - Product JSON data
     */
    function assignVariantsMedia(productJson) {
        productMedia = productJson.media;
        
        // Process each variant to build mapping structures
        productJson.variants.forEach(function(variant) {
            // Handle featured images
            if (variant.featured_image && variantAssignedImagesIds.indexOf(variant.featured_image.id) === -1) {
                variantAssignedImagesIds.push(variant.featured_image.id);
                variantAssignedImages.push(variant.featured_image);
            }
            
            // Handle featured media
            if (variant.featured_media && variantAssignedMediaIds.indexOf(variant.featured_media.id) === -1) {
                variantAssignedMediaIds.push(variant.featured_media.id);
                variantAssignedMedia.push(variant.featured_media);
            }
            
            // Set default variant
            if (!defaultVariant && variant.available) {
                defaultVariant = variant.id;
            }
            
            // Build media mapping
            if (variant.featured_media) {
                if (!allVariantMedia[variant.featured_media.id]) {
                    allVariantMedia[variant.featured_media.id] = [];
                }
                allVariantMedia[variant.featured_media.id].push(variant.id);
            }
            
            // Build image mapping
            if (variant.featured_image) {
                if (!allVariantImages[variant.featured_image.id]) {
                    allVariantImages[variant.featured_image.id] = [];
                }
                allVariantImages[variant.featured_image.id].push(variant.id);
            }
            
            // Initialize variant arrays
            variantsMedia[variant.id] = [];
            variants[variant.id] = [];
            productVariants[variant.id] = variant;
        });
        
        // Process media to determine general vs variant-specific
        let generalMedia = true;
        let currentVariantIds = [];
        
        productMedia.forEach(function(media, index) {
            if (generalMedia && variantAssignedMediaIds.indexOf(media.id) === -1) {
                // This is general media (not assigned to any variant)
                if (CONFIG.skipShowcaseImage > 0 && index < CONFIG.skipShowcaseImage) {
                    variantsMedia.skipped.push(media);
                } else {
                    variantsMedia.g.push(media);
                }
            } else {
                // This is variant-specific media
                generalMedia = false;
                currentVariantIds = allVariantMedia[media.id] ? allVariantMedia[media.id] : currentVariantIds;
                
                currentVariantIds.forEach(function(variantId) {
                    variantsMedia[variantId].push(media);
                });
            }
        });
    }
    
    /**
     * Assign variant images (similar to media but for images)
     * @param {Array} images - Product images array
     */
    function assignVariantImages(images) {
        productImages = images;
        let generalImages = true;
        let currentVariantIds = [];
        
        productImages.forEach(function(image, index) {
            if (generalImages && variantAssignedImagesIds.indexOf(image.id) === -1) {
                // General image
                if (CONFIG.skipShowcaseImage > 0 && index < CONFIG.skipShowcaseImage) {
                    variants.skipped.push(image);
                } else {
                    variants.g.push(image);
                }
            } else {
                // Variant-specific image
                generalImages = false;
                currentVariantIds = allVariantImages[image.id] ? allVariantImages[image.id] : currentVariantIds;
                
                currentVariantIds.forEach(function(variantId) {
                    variants[variantId].push(image);
                });
            }
        });
    }
    
    /**
     * Find variant images for a specific variant ID
     * @param {string|number} variantId - Variant ID to find images for
     * @returns {Array} Array of images for the variant
     */
    function findVariantImages(variantId) {
        if (!variantId) {
            variantId = findCurrentVariant();
        }
        
        let variantImages;
        
        if (variants.hasOwnProperty(variantId)) {
            variantImages = variants[variantId];
        } else {
            variantId = findCurrentVariantFromUrl();
            variantImages = variants.hasOwnProperty(variantId) ? variants[variantId] : variants[defaultVariant];
        }
        
        // Return all images if no variant images or skip showcase exceeds product images
        if (!variantImages || variantImages.length === 0 || CONFIG.skipShowcaseImage >= productImages.length) {
            return productImages;
        }
        
        // Handle special URL behavior cases
        if (!pageDetection.isHome && CONFIG.onlyRunIfVariantInUrl !== "no_action" && !isVariantInUrl()) {
            const showcaseImages = () => variants.g.length ? variants.g : variantAssignedImages;
            const skippedAndShowcaseImages = () => {
                const images = variants.skipped.concat(variants.g);
                return images.length ? images : variantAssignedImages;
            };
            
            selectionVid = 0;
            
            switch (CONFIG.onlyRunIfVariantInUrl) {
                case "showcase_only":
                    return showcaseImages();
                case "showcase_and_variants":
                    return variants.g.concat(variantAssignedImages);
                case "featured_only":
                    return [productImages[0]];
                case "featured_and_showcase":
                    return CONFIG.skipShowcaseImage > 0 ? 
                        [productImages[0]].concat(variants.g) : showcaseImages();
                case "skipped_and_showcase":
                    return skippedAndShowcaseImages();
                case "show_all":
                    return productImages;
            }
        }
        
        // Apply general images positioning
        if (CONFIG.generalImagesPosition === "at_start") {
            variantImages = variants.g.concat(variantImages);
        } else if (CONFIG.generalImagesPosition === "at_end") {
            variantImages = variantImages.concat(variants.g);
        }
        
        return variantImages && variantImages.length !== 0 ? variantImages : productImages;
    }
    
    /**
     * Find variant media for a specific variant ID
     * @param {string|number} variantId - Variant ID to find media for
     * @returns {Array} Array of media for the variant
     */
    function findVariantMedia(variantId) {
        if (!variantId) {
            variantId = findCurrentVariant();
        }
        
        let variantMedia;
        
        if (variantsMedia.hasOwnProperty(variantId)) {
            variantMedia = variantsMedia[variantId];
        } else {
            variantId = findCurrentVariantFromUrl();
            variantMedia = variantsMedia.hasOwnProperty(variantId) ? 
                variantsMedia[variantId] : variantsMedia[defaultVariant];
        }
        
        if (!variantMedia || variantMedia.length === 0 || CONFIG.skipShowcaseImage >= productMedia.length) {
            return productMedia;
        }
        
        // Handle special URL behavior cases (similar to findVariantImages)
        if (!pageDetection.isHome && CONFIG.onlyRunIfVariantInUrl !== "no_action" && !isVariantInUrl()) {
            const showcaseImages = () => variantsMedia.g.length ? variantsMedia.g : variantAssignedMedia;
            const skippedAndShowcaseImages = () => {
                const images = variantsMedia.skipped.concat(variantsMedia.g);
                return images.length ? images : variantAssignedMedia;
            };
            
            selectionVid = 0;
            
            switch (CONFIG.onlyRunIfVariantInUrl) {
                case "showcase_only":
                    return showcaseImages();
                case "showcase_and_variants":
                    return variantsMedia.g.concat(variantAssignedMedia);
                case "featured_only":
                    return [productMedia[0]];
                case "featured_and_showcase":
                    return CONFIG.skipShowcaseImage > 0 ? 
                        [productMedia[0]].concat(variantsMedia.g) : showcaseImages();
                case "skipped_and_showcase":
                    return skippedAndShowcaseImages();
                case "show_all":
                    return productMedia;
            }
        }
        
        // Apply general images positioning
        if (CONFIG.generalImagesPosition === "at_start") {
            variantMedia = variantsMedia.g.concat(variantMedia);
        } else if (CONFIG.generalImagesPosition === "at_end") {
            variantMedia = variantMedia.concat(variantsMedia.g);
        }
        
        const result = variantMedia && variantMedia.length !== 0 ? variantMedia : productMedia;
        return CONFIG.priorityVideo ? moveVideoToFront(result) : result;
    }
    
    // ========================================
    // VALIDATION AND RESTRICTION FUNCTIONS
    // ========================================
    

    
    /**
     * Check if product should be restricted based on tags
     * @returns {boolean} True if product should be restricted
     */
    function isRestrictable() {
        const enabledTags = /via_enabled|viaenabled|via-enabled|__via_enabled/;
        const disabledTags = /via_disabled|via-disabled|viadisabled|__via_disabled/;
        
        return (
            (CONFIG.limitOnProduct === "include_selected" && 
             !enabledTags.test(productTags.toLowerCase())) ||
            (CONFIG.limitOnProduct === "exclude_selected" && 
             disabledTags.test(productTags.toLowerCase()))
        );
    }
    
    /**
     * Check if product has no variants (default title product)
     * @returns {boolean} True if no variants
     */
    function isNoVariantProduct() {
        return productJson.variants.length === 1 && 
               productJson.variants[0].title.toLowerCase().includes("default");
    }
    
    /**
     * Initialize and execute the variant image system
     */
    function init() {
        // Check if we can execute the script
        const canExecute = (
            pageDetection.isHome || 
            pageDetection.isPages || 
            pageDetection.isCollection || 
            pageDetection.isProductPage || 
            pageDetection.isProductPreview
        );
        
        if (!canExecute) {
            console.log("Show Variant Images: Page not supported");
            return;
        }
        
        console.log("🚀 Show Variant Images: Initializing...");
        
        // Set product URL based on page type
        if (pageDetection.isProductPage || pageDetection.isProductPreview) {
            productUrl = window.location.pathname.replace(/\/$/, "").replace("//", "/");
        }
        
        // Start the main execution
        if (productUrl.length && (
            window.via_product_handle === undefined || 
            window.via_product_handle !== productUrl
        )) {
            window.via_product_handle = productUrl;
            
            // Always fetch from Storefront API
            fetchProductDataFromStoreFront(() => {
                console.log("✅ Show Variant Images: Data loaded, processing...");
                processProductData();
            });
        }
    }
    
    /**
     * Process loaded product data and execute main app logic
     */
    function processProductData() {
        productTags = productJson.tags.join();
        // Always use storefront API format (productJson.images)
        const images = productJson.images;
        
        assignVariantsMedia(productJson);
        assignVariantImages(images);
        
        // Preload all thumbnail images for faster switching
        preloadThumbnails(productMedia);
        
        // Add thumbnail optimization CSS for faster rendering
        addCSS(`
            .product__thumbs--scroller {
                will-change: contents;
                transform: translateZ(0);
            }
            .product__thumb-item {
                will-change: transform;
                backface-visibility: hidden;
            }
            .product__thumb-item img {
                transform: translateZ(0);
                will-change: auto;
            }
        `);
        
        executeAppCode();
    }
    
    /**
     * Main app execution logic
     */
    function executeAppCode() {
        // Check restrictions
        if (isRestrictable() || 
            (productMedia.length === 1 && productImages.length === 1) || 
            isNoVariantProduct()) {
            console.log("Show Variant Images: Product restrictions apply, skipping...");
            return;
        }
        
        console.log("🎯 Show Variant Images: Starting gallery initialization...");
        
                 // Wait for DOM to be ready
         const executionTimer = setInterval(() => {
             if (document.readyState === "interactive" || document.readyState === "complete") {
                 clearInterval(executionTimer);
                 initializeGallerySystem();
             }
         }, 10);
     }
    
    // ========================================
    // GALLERY SYSTEM FUNCTIONS
    // ========================================
    
    /**
     * Initialize the gallery backup and manipulation system
     */
    function initializeGallerySystem() {
        const isMobile = (window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth) < 769;
        
        // Add mobile-specific CSS for certain stores
        if (window.Shopify.shop === "amichi-co.myshopify.com" && isMobile) {
            addCSS(".product__main-photos .flickity-page-dots {opacity: 0;}");
        }
        
        // Check for new Impulse theme version
        const newVersion = document.querySelector(".product__thumbs--scroller");
        const section = document.querySelector('[data-section-type="product"]') || 
                       document.querySelector('[data-section-type="product-template"]');
        
        if (!section) {
            console.error("Show Variant Images: Product section not found");
            return;
        }
        
        const sectionId = section.getAttribute("data-section-id");
        
        if (newVersion) {
            // Modern Impulse theme (Flickity-based)
            initializeModernGallery(section, sectionId);
        } else {
            console.log("Show Variant Images: Legacy theme support not implemented in beautified version");
            // Legacy theme support would go here if needed
        }
    }
    
    /**
     * Initialize modern gallery system (Flickity-based)
     * @param {Element} section - Product section element
     * @param {string} sectionId - Section ID
     */
    function initializeModernGallery(section, sectionId) {
        console.log("🖼️ Show Variant Images: Initializing modern gallery...");
        
        // Create backup containers
        createElementAppendToBody("div", "multi-variant-info");
        createElementAppendToBody("div", "multi-variant-thumb");
        createElementAppendToBody("div", "multi-variant-desktop");
        
        // Map product media to slides and create backups
        productMedia.forEach((media, index) => {
            const slides = section.querySelectorAll(".product-slideshow .product-main-slide");
            if (slides[index]) {
                slides[index].setAttribute("data-id", media.id);
            }
        });
        
        // Backup original gallery content
        const slideshowElement = section.querySelector(".product-slideshow");
        const thumbsElement = section.querySelector(".product__thumbs--scroller");
        const desktopElement = section.querySelector(".product-images-desktop");
        
        if (slideshowElement) {
            document.getElementById("multi-variant-info").innerHTML = slideshowElement.innerHTML;
        }
        
        if (thumbsElement) {
            document.getElementById("multi-variant-thumb").innerHTML = thumbsElement.innerHTML;
        }
        
        if (desktopElement) {
            document.getElementById("multi-variant-desktop").innerHTML = desktopElement.innerHTML;
        }
        
        // Fix lazy loading classes in backups
        fixLazyLoadingInBackups();
        
        // Wait for theme.Slideshow to be available
        const themeTimer = setInterval(() => {
            if (typeof theme === "object" && typeof theme.Slideshow === "function") {
                clearInterval(themeTimer);
                
                // Turn off any existing variant image change events
                section.off?.("variantImageChange.product-" + sectionId);
                
                // Set up variant change event listeners
                setupVariantChangeListeners(section, sectionId);
                
                // Initial gallery setup
                ChangeImages();
                
            }
        }, 20);
    }
    
    /**
     * Fix lazy loading classes in backup containers
     */
    function fixLazyLoadingInBackups() {
        const containers = ["#multi-variant-info", "#multi-variant-thumb", "#multi-variant-desktop"];
        
        containers.forEach(container => {
            const images = document.querySelectorAll(`${container} img.lazyloading`);
            images.forEach(image => {
                image.classList.remove("lazyloading");
                image.classList.add("lazyload");
            });
        });
    }
    
    /**
     * Set up variant change event listeners
     * @param {Element} section - Product section element
     * @param {string} sectionId - Section ID
     */
    function setupVariantChangeListeners(section, sectionId) {
        // Find all variant selector elements
        const selectors = document.querySelectorAll(`
            select, 
            label, 
            .swatches, 
            .product-single__form, 
            .variant-wrapper .variant-input, 
            form[action="/cart/add"]
        `);
        
        /**
         * Handle variant change events
         */
        function variantChangeEvent() {
            setTimeout(() => {
                const newVariantId = findCurrentVariant();
                if (selectionVid !== newVariantId) {
                    console.log(`🔄 Show Variant Images: Variant changed to ${newVariantId}`);
                    ChangeImages(newVariantId);
                    selectionVid = newVariantId;
                }
            }, 100);
        }
        
        // Add event listeners to all selectors
        if (selectors) {
            selectors.forEach((selector) => {
                selector.addEventListener("click", variantChangeEvent, false);
                selector.addEventListener("change", variantChangeEvent, false);
            });
        }
    }
    
    /**
     * Main function to change gallery images for Impulse theme
     * @param {string|number} variantId - Variant ID to show images for
     */
    function ChangeImages(variantId) {
        const variantImages = findVariantMedia(variantId);
        
        // Check if images have actually changed
        if (isArrayEqual(lastVariantImages, variantImages)) {
            return;
        }
        
        lastVariantImages = variantImages;
        
        console.log(`🖼️ Show Variant Images: Changing to ${variantImages.length} images for variant ${variantId}`);
        
        const section = document.querySelector('[data-section-type="product"]') || 
                       document.querySelector('[data-section-type="product-template"]');
        
        // Handle mobile height consistency for specific stores
        const isMobile = (window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth) < 769;
        if (window.Shopify.shop === "amichi-co.myshopify.com" && isMobile) {
            const productImagesContainer = section.querySelector("[data-product-images]");
            if (productImagesContainer) {
                const mainImageHeight = productImagesContainer.offsetHeight || 
                                      section.querySelector("[data-product-images]")?.offsetHeight || 0;
                productImagesContainer.style.height = `${mainImageHeight}px`;
            }
        }
        
        // Build new gallery content with optimizations
        let slideImages = "";
        let thumbImages = "";
        
        // Use optimized thumbnail building for faster performance
        thumbImages = buildThumbnailsOptimized(variantImages);
        
        // Build slide images with optimized DOM operations
        variantImages.forEach((image, index) => {
            let isSkip = false;
            const main = document.querySelector(`#multi-variant-info .product-main-slide[data-id="${image.id}"]`);
            
            if (main) {
                // Update slide attributes
                main.setAttribute("data-index", index);
                main.querySelector(".photoswipe__image")?.setAttribute("data-index", index + 1);
                main.removeAttribute("style");
                
                // Handle model viewers
                if (image.media_type === "model") {
                    const modelContainer = main.querySelector("[data-product-media-type-model]");
                    if (modelContainer) {
                        const modelViewer = main.querySelector("model-viewer");
                        if (modelViewer) {
                            modelContainer.innerHTML = modelViewer.outerHTML;
                        }
                    }
                }
                
                // Special handling for specific stores
                if (window.Shopify.shop === "solli-s-hundebedarf.myshopify.com") {
                    const hiddenImg = main.querySelector('img[alt="hidden"]');
                    isSkip = hiddenImg;
                }
                
                if (!isSkip) {
                    slideImages += main.outerHTML;
                }
            }
        });
        
        // Use requestAnimationFrame for smooth DOM updates
        requestAnimationFrame(() => {
            // Destroy existing Flickity instance
            if (window.Flickity && window.Flickity.data(".product-slideshow")) {
                window.Flickity.data(".product-slideshow").destroy();
            }
            
            // Batch DOM updates for better performance
            const slideshowContainer = section.querySelector(".product-slideshow");
            const thumbsContainer = section.querySelector(".product__thumbs--scroller");
            
            // Use document fragment for faster DOM manipulation
            if (slideshowContainer && slideImages) {
                slideshowContainer.style.visibility = "hidden";
                slideshowContainer.innerHTML = slideImages;
                slideshowContainer.style.visibility = "visible";
            }
            
            if (thumbsContainer && thumbImages) {
                thumbsContainer.style.visibility = "hidden";
                thumbsContainer.innerHTML = thumbImages;
                thumbsContainer.style.visibility = "visible";
            }
            
            // Continue with gallery initialization after DOM updates
            finalizeThumbnailUpdate(section, variantImages);
        });
    }
    
    /**
     * Initialize new gallery with Flickity and other features
     * @param {Element} section - Product section element
     */
    function initializeNewGallery(section) {
        const container = section;
        const sectionId = container.getAttribute("data-section-id");
        const inModal = container.dataset.modal === "true";
        
        // Gallery settings
        const settings = {
            enableHistoryState: container.dataset.history || false,
            namespace: ".product-" + sectionId,
            inventory: container.dataset.inventory || false,
            incomingInventory: container.dataset.incomingInventory || false,
            modalInit: false,
            hasImages: true,
            imageSetName: null,
            imageSetIndex: null,
            currentImageSet: null,
            imageSize: "620x",
            currentSlideIndex: 0,
            videoLooping: container.dataset.videoLooping
        };
        
        if (inModal) {
            settings.enableHistoryState = false;
            settings.namespace = ".product-" + sectionId + "-modal";
        }
        
        // Element selectors
        const selectors = {
            media: "[data-product-media-type-model]",
            closeMedia: ".product-single__close-media",
            photoThumbs: ".product__thumb-" + sectionId,
            thumbSlider: "#ProductThumbs-" + sectionId,
            photoThumbs2: "[data-product-thumb]",
            thumbSlider2: "[data-product-thumbs]",
            thumbScroller: ".product__thumbs--scroller",
            mainSlider: "#ProductPhotos-" + sectionId,
            mainSlider2: "[data-product-photos]",
            imageContainer: "[data-product-images]",
            productImageMain: ".product-image-main--" + sectionId,
            productVideo: ".product__video",
            videoParent: ".product__video-wrapper",
            slide: ".product-main-slide",
            currentSlide: ".is-selected",
            startingSlide: ".starting-slide"
        };
        
        // Cache DOM elements
        const cache = {
            mainSlider: container.querySelector(selectors.mainSlider) || 
                       container.querySelector(selectors.mainSlider2),
            thumbSlider: container.querySelector(selectors.thumbSlider) || 
                        container.querySelector(selectors.thumbSlider2),
            thumbScroller: container.querySelector(selectors.thumbScroller),
            productImageMain: container.querySelector(selectors.productImageMain)
        };
        
        // Initialize components
        initProductSlider(cache, settings, selectors, sectionId);
        initImageZoom(section, selectors, sectionId);
        
        // Initialize video modal if available
        if (typeof theme.videoModal === "function") {
            theme.videoModal();
        }
    }
    
    /**
     * Initialize the main product slider (Flickity)
     * @param {Object} cache - Cached DOM elements
     * @param {Object} settings - Gallery settings
     * @param {Object} selectors - Element selectors
     * @param {string} sectionId - Section ID
     */
    function initProductSlider(cache, settings, selectors, sectionId) {
        if (!cache.mainSlider) return;
        
        const mainSliderArgs = {
            dragThreshold: 25,
            adaptiveHeight: true,
            avoidReflow: true,
            initialIndex: settings.currentSlideIndex,
            childNav: cache.thumbSlider,
            childNavScroller: cache.thumbScroller,
            childVertical: cache.thumbSlider && cache.thumbSlider.dataset.position === "beside",
            pageDots: true,
            wrapAround: false,
            callbacks: {
                onChange: function(index) {
                    onSlideChange(index, cache, selectors, settings);
                }
            }
        };
        
        // Initialize Flickity
        const flickity = new theme.Slideshow(cache.mainSlider, mainSliderArgs);
        
        // Handle initial setup
        setTimeout(() => {
            const prevArrow = cache.mainSlider.parentElement.querySelector(".product__thumb-arrow.product__thumb-arrow--prev");
            if (prevArrow) {
                prevArrow.classList.add("hide");
            }
            
            if (mainSliderArgs.childVertical && cache.thumbScroller) {
                cache.thumbScroller.scrollTop = 0;
            } else if (cache.thumbScroller) {
                cache.thumbScroller.scrollLeft = 0;
            }
        }, 100);
    }
    
    /**
     * Handle slide change events
     * @param {number} index - New slide index
     * @param {Object} cache - Cached DOM elements
     * @param {Object} selectors - Element selectors
     * @param {Object} settings - Gallery settings
     */
    function onSlideChange(index, cache, selectors, settings) {
        if (!cache.mainSlider) return;
        
        const allSlides = cache.mainSlider.querySelectorAll(selectors.slide);
        const prevSlide = allSlides[settings.currentSlideIndex];
        const nextSlide = allSlides[index];
        
        if (prevSlide) {
            prevSlide.setAttribute("tabindex", "-1");
        }
        
        if (nextSlide) {
            nextSlide.setAttribute("tabindex", "0");
        }
        
        settings.currentSlideIndex = index;
    }
    
    /**
     * Initialize image zoom functionality
     * @param {Element} section - Product section element
     * @param {Object} selectors - Element selectors
     * @param {string} sectionId - Section ID
     */
    function initImageZoom(section, selectors, sectionId) {
        const container = section.querySelector(selectors.imageContainer);
        if (!container) return;
        
        if (theme && typeof theme.Photoswipe === "function") {
            new theme.Photoswipe(container, sectionId);
            
            // Listen for photoswipe changes to sync with gallery
            container.addEventListener("photoswipe:afterChange", function(evt) {
                // Could sync gallery here if needed
            });
        }
    }
    
    // ========================================
    // PUBLIC API
    // ========================================
    
    return {
        init: init,
        CONFIG: CONFIG,
        // Expose utility functions for debugging
        convertLanguageCode: convertLanguageCode,
        extractNumericId: extractNumericId,
        findCurrentVariant: findCurrentVariant,
        isArrayEqual: isArrayEqual
    };
};

// Initialize the system
const showVariantImages = ShowVariantImages();

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', showVariantImages.init);
} else {
    showVariantImages.init();
}

// Export for global access
window.ShowVariantImages = showVariantImages;
